/**
 * Trust Badges Dynamic Delivery Date Calculator
 * Calculates estimated delivery date based on business days
 * Includes countdown timer to shipping cutoff (4pm EST)
 */

// the script is loaded by both the product page and the cart, so only define it once
if ( ! window.TrustBadgesDate ) {

class TrustBadgesDate {
  constructor(root = document) {
    this.cutoffHour = 16; // 4pm
    this.cutoffTimezone = 'America/New_York'; // EST/EDT
    this.updateIntervals = [];
    this.root = root;
    this.init();
  }

  init() {
    // Find all elements with dynamic date markers
    const dateElements = this.root.querySelectorAll('[data-trust-badge-date]');

    dateElements.forEach(element => {
      // cart estimates are handled by their own <delivery-estimate> element
      if ( this.root === document && element.closest('delivery-estimate') ) return;

      const businessDays = parseInt(element.getAttribute('data-business-days') || '2');
      const format = element.getAttribute('data-date-format') || 'full';
      const prefix = element.getAttribute('data-date-prefix') || 'Get it by';
      const showCountdown = element.getAttribute('data-show-countdown') === 'true';

      this.updateDeliveryInfo(element, businessDays, format, prefix, showCountdown);

      // If countdown is enabled, check every second so the last minute can count down in seconds
      // and the row goes away right at the cutoff (the text is only rewritten when it changes)
      if (showCountdown) {
        const interval = setInterval(() => {
          this.updateDeliveryInfo(element, businessDays, format, prefix, showCountdown);
        }, 1000);
        this.updateIntervals.push(interval);
      }
    });
  }

  updateDeliveryInfo(element, businessDays, format, prefix, showCountdown) {
    const { isBeforeCutoff, hoursRemaining, minutesRemaining, secondsRemaining, estDate } = this.getTimeUntilCutoff();

    // Orders ship the same day on weekdays before the cutoff; otherwise on the next weekday,
    // so there is no countdown on weekends and the date counts from the real ship day
    const isWeekday = date => date.getDay() !== 0 && date.getDay() !== 6;
    const shipsToday = isBeforeCutoff && isWeekday(estDate);
    let shipDate = new Date(estDate);
    if (!shipsToday) {
      do {
        shipDate.setDate(shipDate.getDate() + 1);
      } while (!isWeekday(shipDate));
    }

    // Calculate delivery date from ship date
    const deliveryDate = this.calculateDeliveryDate(businessDays, shipDate);
    const formattedDate = this.formatDate(deliveryDate, format);

    if (element.getAttribute('data-style') === 'cart') {
      // Amazon-style cart line: "FREE delivery Mon, Oct 5. Order within 1 hr 46 mins", then "45 secs" in the last minute
      let html = `${prefix} <strong class="trust-badge__date">${formattedDate}</strong>`;
      if (showCountdown && shipsToday) {
        const count = (n, unit) => `${n} ${unit}${n === 1 ? '' : 's'}`;
        const countdown = hoursRemaining > 0 ? `${hoursRemaining} hr${minutesRemaining ? ` ${count(minutesRemaining, 'min')}` : ''}` :
          minutesRemaining > 0 ? count(minutesRemaining, 'min') : count(secondsRemaining, 'sec');
        const urgency = hoursRemaining > 0 ? '' : ' trust-badge__countdown--urgent';
        html += `. Order within <span class="trust-badge__countdown${urgency}">${countdown}</span>`;
      }
      this.setHtml(element, html);
      return;
    }

    // Product page: one badge row each for the free date and the fastest paid date, with the Halloween
    // or weekend note under the date that makes it, plus the countdown while an order still ships today:
    // "Free delivery Tue, Oct 27 / Arrives before Halloween", "Fastest Mon, Oct 26", "Order within 4h 59m to ship today"
    const fastestDays = parseInt(element.getAttribute('data-fastest-days') || '0');
    const fastestDate = fastestDays > 0 && fastestDays < businessDays ? this.calculateDeliveryDate(fastestDays, shipDate) : null;
    const occasion = this.getOccasion(estDate, deliveryDate);
    const fastestOccasion = !occasion && fastestDate ? this.getOccasion(estDate, fastestDate) : '';
    const note = name => name ? `<span class="trust-badge__occasion">Arrives before ${name}</span>` : '';

    let fastest = '';
    if (fastestDate) {
      const price = element.getAttribute('data-fastest-price');
      fastest = `${element.getAttribute('data-fastest-label') || 'Fastest'} <span class="trust-badge__date">${this.formatDate(fastestDate, format)}</span>` +
        (price ? ` <span class="trust-badge__price">(${price})</span>` : '') + note(fastestOccasion);
    }

    // "Ships tomorrow" adds nothing to the delivery date, so the row only shows the countdown
    let ships = '';
    if (showCountdown && shipsToday) {
      // short form so the line fits a phone: "4h 59m", "6h", "12m", then "45s" in the last minute
      const countdown = hoursRemaining > 0 ? `${hoursRemaining}h${minutesRemaining ? ` ${minutesRemaining}m` : ''}` :
        minutesRemaining > 0 ? `${minutesRemaining}m` : `${secondsRemaining}s`;
      // orange in the last hour, like the cart drawer
      const urgency = hoursRemaining > 0 ? '' : ' class="trust-badge__countdown--urgent"';
      ships = `Order within <strong${urgency}>${countdown}</strong> to ship today`;
    }

    this.fillRow(element, 'free', `${prefix} <span class="trust-badge__date">${formattedDate}</span>${note(occasion)}`);
    this.fillRow(element, 'fastest', fastest);
    this.fillRow(element, 'ships', ships);
  }

  // Fills one row of the product page delivery block, and hides the row when it has nothing to say
  fillRow(element, name, html) {
    const target = element.querySelector(`[data-delivery-${name}]`);
    if (!target) return;
    this.setHtml(target, html);
    target.closest('.trust-badge').hidden = !html;
  }

  // The countdown checks every second, so only touch the DOM when the text actually changes
  setHtml(target, html) {
    if (target.trustBadgeHtml === html) return;
    target.trustBadgeHtml = html;
    target.innerHTML = html;
  }

  getTimeUntilCutoff() {
    try {
      // Get current time in EST
      const now = new Date();

      // Get EST time components
      const estTimeString = now.toLocaleString('en-US', {
        timeZone: this.cutoffTimezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });

      // Parse the EST time string (format: MM/DD/YYYY, HH:mm:ss)
      const [datePart, timePart] = estTimeString.split(', ');
      const [month, day, year] = datePart.split('/');
      const [hours, minutes, seconds] = timePart.split(':');

      // Create a Date object representing EST time
      const estDate = new Date(year, month - 1, day, hours, minutes, seconds);

      // Create cutoff time for today in EST
      const cutoffToday = new Date(estDate);
      cutoffToday.setHours(this.cutoffHour, 0, 0, 0);

      // Calculate difference in milliseconds
      const diffMs = cutoffToday - estDate;

      // Check if we're before cutoff
      const isBeforeCutoff = diffMs > 0;

      // Time remaining, rounded up so it never reads "0m": "1m" until the last minute, which counts down in seconds
      const lastMinute = diffMs < 60000;
      const minutesLeft = lastMinute ? 0 : Math.ceil(diffMs / 60000);

      return {
        isBeforeCutoff,
        hoursRemaining: Math.floor(minutesLeft / 60),
        minutesRemaining: minutesLeft % 60,
        secondsRemaining: lastMinute ? Math.max(0, Math.ceil(diffMs / 1000)) : 0,
        estDate: estDate
      };
    } catch (error) {
      // Fallback if timezone conversion fails
      console.error('Error calculating cutoff time:', error);
      const now = new Date();
      return { isBeforeCutoff: true, hoursRemaining: 0, minutesRemaining: 0, estDate: now };
    }
  }

  // "Halloween" in the two weeks before it, when the order arrives the day before or earlier;
  // otherwise "the weekend" when an order placed Monday to Thursday arrives by Friday
  getOccasion(today, deliveryDate) {
    const halloween = new Date(today.getFullYear(), 9, 31);
    const daysToHalloween = (halloween - today) / 86400000;
    if (daysToHalloween > 0 && daysToHalloween <= 14) {
      return deliveryDate < halloween ? 'Halloween' : '';
    }

    const day = today.getDay();
    if (day >= 1 && day <= 4) {
      const friday = new Date(today);
      friday.setDate(friday.getDate() + 5 - day);
      friday.setHours(23, 59, 59, 999);
      if (deliveryDate <= friday) return 'the weekend';
    }
    return '';
  }

  calculateDeliveryDate(businessDays, startDate) {
    let daysAdded = 0;
    let currentDate = new Date(startDate);

    while (daysAdded < businessDays) {
      currentDate.setDate(currentDate.getDate() + 1);

      // Skip weekends (0 = Sunday, 6 = Saturday)
      const dayOfWeek = currentDate.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        daysAdded++;
      }
    }

    return currentDate;
  }

  formatDate(date, format) {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const dayName = days[date.getDay()];
    const monthName = months[date.getMonth()];
    const dayNumber = date.getDate();

    switch (format) {
      case 'full':
        return `${dayName}, ${monthName} ${dayNumber}`;
      case 'short':
        return `${monthName} ${dayNumber}`;
      case 'short-day':
        return `${dayName.slice(0, 3)}, ${monthName} ${dayNumber}`;
      case 'relative':
        return dayName;
      default:
        return `${dayName}, ${monthName} ${dayNumber}`;
    }
  }

  destroy() {
    // Clean up intervals when needed
    this.updateIntervals.forEach(interval => clearInterval(interval));
    this.updateIntervals = [];
  }
}

window.TrustBadgesDate = TrustBadgesDate;

// Cart delivery estimate: re-rendered with the cart, so it initializes itself
customElements.define('delivery-estimate', class DeliveryEstimate extends HTMLElement {
  connectedCallback() {
    this.dates = new TrustBadgesDate(this);
  }
  disconnectedCallback() {
    this.dates.destroy();
  }
});

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new TrustBadgesDate();
  });
} else {
  new TrustBadgesDate();
}

}
